import { supabase } from '@/integrations/supabase/client';
import { List, ListMember, CreateListRequest, AddCandidatesRequest, AISearchRequest } from '@/types/list';

// Helper function to get current user's organization
const getCurrentOrganization = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: userOrgs } = await supabase
    .from('user_organization')
    .select('organization_uuid')
    .eq('user_uuid', user.id)
    .single();

  if (!userOrgs) throw new Error('User not associated with any organization');
  return userOrgs.organization_uuid;
};

// Helper function to get current user
const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  return user.id;
};

export const listService = {
  // Get all lists for the current organization
  async getLists(): Promise<List[]> {
    const organizationUuid = await getCurrentOrganization();
    
    const { data, error } = await supabase
      .from('lists_with_candidate_count')
      .select('id, name, description, custom_fields, created_at, updated_at, created_by, candidate_count')
      .eq('organization_uuid', organizationUuid)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get a specific list by ID
  async getList(id: string): Promise<List> {
    const { data, error } = await supabase
      .from('lists_with_candidate_count')
      .select('id, name, description, custom_fields, created_at, updated_at, created_by, candidate_count')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Create a new list
  async createList(request: CreateListRequest): Promise<List> {
    const organizationUuid = await getCurrentOrganization();
    const userId = await getCurrentUser();

    const { data, error } = await supabase
      .from('lists')
      .insert({
        name: request.name,
        description: request.description,
        custom_fields: request.custom_fields || {},
        created_by: userId,
        organization_uuid: organizationUuid
      })
      .select('*')
      .single();

    if (error) throw error;
    
    // Return with candidate count
    return {
      ...data,
      candidate_count: 0
    };
  },

  // Update a list
  async updateList(id: string, updates: Partial<CreateListRequest>): Promise<List> {
    const { data, error } = await supabase
      .from('lists')
      .update({
        name: updates.name,
        description: updates.description,
        custom_fields: updates.custom_fields,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;
    
    // Get candidate count
    const { count } = await supabase
      .from('list_members')
      .select('*', { count: 'estimated', head: true })
      .eq('list_id', id);

    return {
      ...data,
      candidate_count: count || 0
    };
  },

  // Delete a list
  async deleteList(id: string): Promise<void> {
    const { error } = await supabase
      .from('lists')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // Get members of a list
  async getListMembers(listId: string): Promise<ListMember[]> {
    const { data, error } = await supabase
      .from('list_members')
      .select(`
        *,
        candidates (
          id,
          name,
          email,
          function,
          skills,
          phone,
          location,
          linkedin
        )
      `)
      .eq('list_id', listId)
      .order('added_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(member => ({
      id: member.id,
      list_id: member.list_id,
      candidate_id: member.candidate_id,
      added_at: member.added_at,
      added_by: member.added_by,
      candidate: member.candidates
    })) || [];
  },

  // Add candidates to a list
  async addCandidatesToList(request: AddCandidatesRequest): Promise<void> {
    const userId = await getCurrentUser();
    
    const membersToAdd = request.candidate_ids.map(candidateId => ({
      list_id: request.list_id,
      candidate_id: candidateId,
      added_by: userId
    }));

    const { error } = await supabase
      .from('list_members')
      .insert(membersToAdd);

    if (error) throw error;
  },

  // Remove a candidate from a list
  async removeCandidateFromList(listId: string, candidateId: string): Promise<void> {
    const { error } = await supabase
      .from('list_members')
      .delete()
      .eq('list_id', listId)
      .eq('candidate_id', candidateId);

    if (error) throw error;
  },

  // Search candidates by AI (mock implementation for now)
  async searchCandidatesByAI(request: AISearchRequest): Promise<any[]> {
    // This would integrate with your AI search functionality
    // For now, return mock data
    const { data: candidates } = await supabase
      .from('candidates')
      .select('id, name, email, function, skills')
      .limit(10);

    return candidates || [];
  },

  // Get all candidates for the organization (excluding those already in the list)
  async getCandidatesNotInList(listId: string): Promise<any[]> {
    // First get all candidates for the organization
    const { data: allCandidates, error: candidatesError } = await supabase
      .from('candidates')
      .select('id, name, email, function, skills, phone, location, linkedin, status')
      .order('name');

    if (candidatesError) throw candidatesError;

    // Then get the candidates already in this list
    const { data: listMembers, error: membersError } = await supabase
      .from('list_members')
      .select('candidate_id')
      .eq('list_id', listId);

    if (membersError) throw membersError;

    // Create a set of candidate IDs already in the list for efficient lookup
    const existingCandidateIds = new Set(listMembers?.map(member => member.candidate_id) || []);

    // Filter out candidates already in the list
    const availableCandidates = allCandidates?.filter(candidate => 
      !existingCandidateIds.has(candidate.id)
    ) || [];

    return availableCandidates;
  }
};
